import type { ApolloClient } from "@apollo/client/core";
import moment, { type Moment } from "moment";
import { AccountBudgetDataDocument, AssetManagementCategoryType, BudgetDataDocument, MetricPeriodType } from "~/graphql/generated/graphql";
import { filterUniqueOn, getSet, groupBy, hasValue, sum } from "~/helpers/common-helpers";
import type { BudgetRecord, Property } from "~/helpers/entities/property-helpers";
import {
  fitDate,
  fitDateString,
  getAccountAggregationKey,
  getDateRangeEntries,
  type AccountCell,
  type AccountRow,
  type AccountsRow,
  type AccountType,
  type Budget,
  type CategoryCell,
  type CategoryRow,
} from "~/helpers/modules/accounts-helpers";

export type BudgetData = {
  periodType: MetricPeriodType;
  periods: Moment[];
  budgetRecords: BudgetRecord[];
};

export type CategoryBudgetData = {
  type: "category";
  categoryCells: CategoryCell[];
} & BudgetData;

export type AccountBudgetData = {
  type: "account";
  accountCells: AccountCell[];
} & BudgetData;

export type BudgetCell = { propertyId: string; period: Moment; budgetId: string; value: number; forecast: number };
export type CategoryBudgetCell = { type: "category"; categoryId: string } & BudgetCell;
export type AccountBudgetCell = { type: "account"; accountKey: string } & BudgetCell;

export const getPeriodBudgetsIntersect = (budgets: Budget[], period: Moment, periodType: MetricPeriodType) => {
  const from = fitDate(moment.utc(period), periodType);
  const to = fitDate(moment.utc(period), periodType, true);

  return budgets.filter((b) => {
    const budgetFrom = fitDateString(b.validFrom, periodType).startOf("month");
    const budgetTo = fitDateString(b.validTo, periodType).endOf("month");

    return from.isBetween(budgetFrom, budgetTo, "day", "[]") || to.isBetween(budgetFrom, budgetTo, "day", "[]");
  });
};

type RunningDataMutable = {
  budgetActualAccumulated: number;
  completedBudgetActualAccumulated: number;
  completedBalance: number | undefined;
};

const getRunningDataMutable = (): RunningDataMutable => ({
  budgetActualAccumulated: 0,
  completedBudgetActualAccumulated: 0,
  completedBalance: undefined,
});

export const setBudgetCellValue = (
  budgetCell: BudgetCell,
  {
    initialBalance,
    accountsCells,
    budgetRecords,
    currentPeriod,
    inputPeriod,
    type,
  }: {
    inputPeriod: Moment;
    currentPeriod: Moment;
    accountsCells: (CategoryCell | AccountCell)[];
    budgetRecords: BudgetRecord[];
    type: AccountType;
    initialBalance: number;
  },
  runningDataMutable: RunningDataMutable
) => {
  const cellsTotal = sum(accountsCells.map((cell) => cell.totalCostWithVat));
  const budgetActual = sum(budgetRecords.map((record) => record.actualCostWithVat));

  const periodIsCompleted = inputPeriod.isBefore(currentPeriod);

  if (!periodIsCompleted && !hasValue(runningDataMutable.completedBalance))
    runningDataMutable.completedBalance = sum(accountsCells.map((cell) => cell.totalCostWithVat - cell.change));

  const forecastPeriod = !periodIsCompleted;

  switch (type) {
    case AssetManagementCategoryType.BalanceSheet:
      budgetCell.value = budgetActual + runningDataMutable.budgetActualAccumulated + initialBalance;

      runningDataMutable.budgetActualAccumulated += budgetActual;

      if (periodIsCompleted) runningDataMutable.completedBudgetActualAccumulated = runningDataMutable.budgetActualAccumulated;

      if (forecastPeriod)
        budgetCell.forecast =
          runningDataMutable.completedBalance! + runningDataMutable.budgetActualAccumulated - runningDataMutable.completedBudgetActualAccumulated;
      break;
    case AssetManagementCategoryType.IncomeStatement:
      budgetCell.value += -1 * budgetActual;

      budgetCell.forecast += forecastPeriod ? -1 * budgetActual : cellsTotal;
      break;
  }
};

export type CategoryDataItem = { id: string; type: AccountType };

export const getCategoryBudgetCells = (
  budget: Budget,
  categories: CategoryDataItem[],
  outDateRange: { from: Moment; to: Moment },
  outputPeriodType: MetricPeriodType,
  data: CategoryBudgetData
) => {
  const inputPeriodType = data.periodType;
  const inputPeriods = data.periods;

  const groupedCells = groupBy(data.categoryCells, {
    period: { type: "period", getKey: (cell) => fitDateString(cell.period, inputPeriodType) },
    categoryId: { type: "string", getKey: (cell) => cell.categoryId },
  });

  const groupedBudgetRecords = groupBy(data.budgetRecords, {
    period: { type: "period", getKey: (cell) => fitDateString(cell.entryDate, inputPeriodType) },
    categoryId: { type: "string", getKey: (cell) => cell.assetManagementAccount?.categoryId },
  });

  const currentPeriod = computed(() => fitDate(moment.utc(), inputPeriodType));

  const budgetCells: CategoryBudgetCell[] = [];

  for (let category of categories) {
    const runningDataMutable = getRunningDataMutable();

    const cellsInitialPeriod = groupedCells.get({ period: inputPeriods[0], categoryId: category.id })?.items ?? [];

    const initialBalance = sum(cellsInitialPeriod.map((cell) => cell.totalCostWithVat - cell.change));

    for (let period of inputPeriods) {
      const cells = groupedCells.get({ categoryId: category.id, period })?.items ?? [];

      const budgetRecords = groupedBudgetRecords.get({ categoryId: category.id, period })?.items ?? [];

      /** Shared cell object. For example if requested period type is quarterly it will match three monthly budget periods */
      const outputPeriod = fitDate(period, outputPeriodType);

      const budgetCell: CategoryBudgetCell = getSet(
        budgetCells,
        (cell) => cell.budgetId === budget.id && category.id === cell.categoryId && outputPeriod.isSame(cell.period),
        () => ({
          type: "category",
          categoryId: category.id,
          budgetId: budget.id,
          propertyId: budget.propertyId!,
          period: outputPeriod,
          value: 0,
          forecast: 0,
        })
      );

      setBudgetCellValue(
        budgetCell,
        {
          accountsCells: cells,
          budgetRecords,
          currentPeriod: currentPeriod.value,
          initialBalance,
          inputPeriod: period,
          type: category.type,
        },
        runningDataMutable
      );
    }
  }

  return budgetCells.filter((cell) => outDateRange.from.isSameOrBefore(cell.period) && outDateRange.to.isSameOrAfter(cell.period));
};

export const getAccountBudgetCells = (
  budget: Budget,
  accountRows: AccountRow[],
  outputPeriodType: MetricPeriodType,
  outDateRange: { from: Moment; to: Moment },
  data: AccountBudgetData
) => {
  const inputPeriodType = data.periodType;
  const inputPeriods = data.periods;

  const groupedCells = groupBy(data.accountCells, {
    period: { type: "period", getKey: (cell) => fitDateString(cell.period, inputPeriodType) },
    accountKey: { type: "string", getKey: getAccountAggregationKey },
  });

  const groupedBudgetRecords = groupBy(data.budgetRecords, {
    period: { type: "period", getKey: (cell) => fitDateString(cell.entryDate, inputPeriodType) },
    accountKey: { type: "string", getKey: (cell) => (cell.assetManagementAccount ? getAccountAggregationKey(cell.assetManagementAccount) : undefined) },
  });

  const currentPeriod = computed(() => fitDate(moment.utc(), inputPeriodType));

  const budgetCells: AccountBudgetCell[] = [];

  for (let accountRow of accountRows) {
    const accountKey = getAccountAggregationKey(accountRow);

    if (!accountKey) continue;

    const runningDataMutable = getRunningDataMutable();

    const cellsInitialPeriod = groupedCells.get({ period: inputPeriods[0], accountKey })?.items ?? [];

    const initialBalance = sum(cellsInitialPeriod.map((cell) => cell.totalCostWithVat - cell.change));

    for (let period of inputPeriods) {
      const cells = groupedCells.get({ period, accountKey })?.items ?? [];

      /** Shared cell object. For example if requested period type is quarterly it will match three monthly budget periods */
      const outputPeriod = fitDate(period, outputPeriodType);

      const budgetCell: AccountBudgetCell = getSet(
        budgetCells,
        (cell) => cell.budgetId === budget.id && cell.accountKey === accountKey && cell.period.isSame(outputPeriod),
        () => ({
          type: "account",
          budgetId: budget.id,
          accountKey,
          period: outputPeriod,
          propertyId: budget.propertyId!,
          value: 0,
          forecast: 0,
        })
      );

      const budgetRecords = groupedBudgetRecords.get({ period, accountKey })?.items ?? [];

      setBudgetCellValue(
        budgetCell,
        {
          accountsCells: cells,
          budgetRecords,
          currentPeriod: currentPeriod.value,
          initialBalance,
          inputPeriod: period,
          type: accountRow.type,
        },
        runningDataMutable
      );
    }
  }

  return budgetCells.filter((cell) => outDateRange.from.isSameOrBefore(cell.period) && outDateRange.to.isSameOrAfter(cell.period));
};

export const getPropertiesBudgets = (properties: Property[], periods: Moment[]) => {
  const ids = new Set<string>();
  const yearsCovered = new Set<number>();
  periods.forEach((period) => {
    const year = period.year();

    if (yearsCovered.has(year)) return;

    yearsCovered.add(year);

    properties.forEach((property) => {
      /**Note Sven: This needs to be changed if we want to handle budgets not conforming to a year */
      const budgets =
        property.assetManagementBudgets?.items?.filter((b) => moment.utc(b.validFrom).year() === year && moment.utc(b.validTo).year() === year) ?? [];

      const budget = budgets.reduce((a, b) => {
        return !a || moment.utc(b.createdAt).isAfter(moment.utc(a.createdAt)) ? b : a;
      }, undefined as Budget | undefined);

      if (budget) ids.add(budget.id);
    });
  });

  return ids;
};

export const getIdFetchKey = (budgets: { id: string }[]) => {
  return budgets
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((b) => b.id)
    .join("_");
};

export const fetchAccountBudgetCells = async (
  client: ApolloClient<any>,
  budget: Budget,
  accounts: AccountRow[],
  period: { from: Moment; to: Moment },
  toPeriodType: MetricPeriodType
) => {
  const budgetBasePeriodType = MetricPeriodType.Monthly;

  const range = {
    from: fitDateString(budget.validFrom, budgetBasePeriodType),
    to: fitDateString(budget.validTo, budgetBasePeriodType).endOf("month"),
  };

  const periods = getDateRangeEntries({ from: range.from, to: range.to }, budgetBasePeriodType);

  if (!accounts.length || !budget.propertyId || !range.from.isValid() || !range.to.isValid()) return;

  const outDateRange = {
    from: moment.max(fitDate(period.from, budgetBasePeriodType), range.from),
    to: moment.min(fitDate(period.to, budgetBasePeriodType), range.to),
  };

  const query = await client.query({
    query: AccountBudgetDataDocument,
    variables: {
      id: budget.id,
      input: {
        from: range.from,
        to: range.to,
        periodType: budgetBasePeriodType,
        accountIds: accounts.map((row) => row.id),
      },
    },
  });

  const budgetRecords = query.data.assetManagementBudget?.assetManagementBudgetRecords?.items ?? [];

  const o: Partial<Record<AccountType, AccountBudgetCell[]>> = {};

  const cellsByAccountType: Record<AccountType, AccountCell[]> = {
    [AssetManagementCategoryType.IncomeStatement]: query.data.incomeStatementAccountSpecificationsByID,
    [AssetManagementCategoryType.BalanceSheet]: query.data.rawBalanceAccountSpecificationsByID,
  };

  const setCells = (accountType: AccountType) => {
    const cells = cellsByAccountType[accountType].filter((c) => c.propertyId === budget.propertyId);

    o[accountType] = getAccountBudgetCells(budget, accounts, toPeriodType, outDateRange, {
      budgetRecords,
      accountCells: cells,
      periods,
      periodType: budgetBasePeriodType,
      type: "account",
    });
  };

  setCells(AssetManagementCategoryType.IncomeStatement);
  setCells(AssetManagementCategoryType.BalanceSheet);

  return o as Record<AccountType, AccountBudgetCell[]>;
};

export const fetchCategoryBudgetCells = async (
  client: ApolloClient<any>,
  budget: Budget,
  outDateRange: { from: Moment; to: Moment } | undefined,
  outPeriodType: MetricPeriodType
): Promise<Record<AccountType, CategoryBudgetCell[]> | undefined> => {
  const budgetBasePeriodType = MetricPeriodType.Monthly;

  const range = {
    from: fitDateString(budget.validFrom, budgetBasePeriodType),
    to: fitDateString(budget.validTo, budgetBasePeriodType).endOf("month"),
  };

  const periods = getDateRangeEntries({ from: range.from, to: range.to }, budgetBasePeriodType);

  if (!budget.propertyId || !range.from.isValid() || !range.to.isValid()) return;

  if (!outDateRange) outDateRange = { from: range.from, to: range.to };

  const query = await client.query({
    query: BudgetDataDocument,
    variables: {
      id: budget.id,
      input: {
        from: range.from,
        to: range.to,
        periodType: budgetBasePeriodType,
        propertyId: budget.propertyId,
      },
    },
  });

  const budgetRecords = query.data.assetManagementBudget?.assetManagementBudgetRecords?.items ?? [];

  const o: Partial<Record<AccountType, CategoryBudgetCell[]>> = {};

  const cellsByAccountType: Record<AccountType, CategoryCell[]> = {
    [AssetManagementCategoryType.IncomeStatement]: query.data.incomeStatementsForProperty,
    [AssetManagementCategoryType.BalanceSheet]: query.data.rawBalancesForProperty,
  };

  const getCategories = (cells: CategoryCell[], type: AccountType) =>
    filterUniqueOn(cells, (cell) => cell.categoryId).map((cell): CategoryDataItem => ({ id: cell.categoryId, type }));

  const setCells = (accountType: AccountType) => {
    const cells = cellsByAccountType[accountType];

    o[accountType] = getCategoryBudgetCells(budget, getCategories(cells, accountType), outDateRange, outPeriodType, {
      budgetRecords,
      categoryCells: cells,
      periods,
      periodType: budgetBasePeriodType,
      type: "category",
    });
  };

  setCells(AssetManagementCategoryType.IncomeStatement);
  setCells(AssetManagementCategoryType.BalanceSheet);

  return o as Record<AccountType, CategoryBudgetCell[]>;
};

export const mapPropertyBudgets = (p: Property) => p.assetManagementBudgets?.items?.map((b) => ({ ...b, propertyId: p.id })) ?? [];
